import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { termsApi } from '@/lib/api'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, Upload, Download, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
const LANGUAGES = [
  { label: '英语 (English)', value: 'en' },
  { label: '中文 (Chinese)', value: 'zh' },
  { label: '日语 (Japanese)', value: 'ja' },
  { label: '韩语 (Korean)', value: 'ko' },
  { label: '法语 (French)', value: 'fr' },
  { label: '德语 (German)', value: 'de' },
]

/**
 * 生成并下载术语导入模板（xlsx 格式）
 *
 * 用 exceljs 而非 CSV 的原因（沿用原 SheetJS 的设计考量）：
 * 1. CSV 即使加 UTF-8 BOM，部分 Excel Mac 版本仍按 GBK 解码导致中文表头乱码
 * 2. 后端 Python engine 用 openpyxl 解析，原生支持 xlsx，CSV 会 500
 * 3. xlsx 是 Excel 原生格式，Mac 预览 / Windows Excel / WPS 均正常显示
 *
 * 由 SheetJS(xlsx) 迁移到 exceljs：npm 上 xlsx 0.18.5 已停止发布、无安全补丁
 * （Prototype Pollution / ReDoS 仅官方 CDN 有修复），而 exceljs 在 npm 正规维护。
 * 本处仅「写出」模板、不解析不可信文件，改用 exceljs。因 exceljs 体积较大，
 * 采用动态 import，仅在用户点击「下载模板」时加载，不进入主打包。
 */
async function downloadTemplate() {
  const headers = ['原文(Source)', '译文(Target)', '源语言(SourceLang)', '目标语言(TargetLang)', '备注(Context)']
  const exampleRows = [
    ['translation', '翻译', 'en', 'zh', '翻译相关术语'],
    ['machine translation', '机器翻译', 'en', 'zh', ''],
    ['artificial intelligence', '人工智能', 'en', 'zh', 'AI缩写'],
    ['quality assurance', '质量保证', 'en', 'zh', ''],
  ]

  // exceljs 动态加载（瘦身主打包，点击下载时才拉取）
  const ExcelJS = await import('exceljs')

  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('术语')
  ws.addRow(headers)
  exampleRows.forEach((row) => ws.addRow(row))
  // 设置列宽，避免表头被挤压（ExcelJS 列序：原文/译文/源语/目标语/备注）
  const colWidths = [28, 28, 18, 18, 28]
  colWidths.forEach((width, idx) => {
    ws.getColumn(idx + 1).width = width
  })

  // 生成 xlsx 二进制并触发浏览器下载
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = '术语导入模板.xlsx'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function TerminologyManager({ clientId }: { clientId: string }) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [newTerm, setNewTerm] = useState({
    source: '',
    target: '',
    sourceLang: 'en',
    targetLang: 'zh',
    context: '',
  })
  // 编辑术语：editingTerm 非 null 时弹出编辑对话框（复用新增对话框的字段结构）
  const [editingTerm, setEditingTerm] = useState<any | null>(null)
  const [editForm, setEditForm] = useState({
    source: '',
    target: '',
    sourceLang: 'en',
    targetLang: 'zh',
    context: '',
  })

  const { data: response, isLoading } = useQuery({
    queryKey: ['terms', clientId],
    queryFn: () => termsApi.getByClientId(clientId)
  })

  const addTerm = useMutation({
    mutationFn: (data: any) => termsApi.create({ ...data, clientId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terms', clientId] })
      toast.success('术语已添加')
      setIsAddDialogOpen(false)
      setNewTerm({ source: '', target: '', sourceLang: 'en', targetLang: 'zh', context: '' })
    },
    onError: (error: any) => {
      toast.error(error.error || '添加失败')
    }
  })

  const deleteTerm = useMutation({
    mutationFn: (id: string) => termsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terms', clientId] })
      toast.success('术语已删除')
    }
  })

  // 更新术语：调用已有 PUT /terms/:id 接口（后端 termSchema.partial() 支持部分字段更新）
  const updateTerm = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => termsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['terms', clientId] })
      toast.success('术语已更新')
      setEditingTerm(null)
    },
    onError: (error: any) => {
      toast.error(error.error || '更新失败')
    }
  })

  // 打开编辑对话框：把当前行术语填入表单
  const openEdit = (term: any) => {
    setEditingTerm(term)
    setEditForm({
      source: term.source || '',
      target: term.target || '',
      sourceLang: term.sourceLang || 'en',
      targetLang: term.targetLang || 'zh',
      context: term.context || '',
    })
  }

  // 保存编辑：只提交可变字段，不带 clientId（防止跨客户篡改）
  const saveEdit = () => {
    if (!editingTerm) return
    updateTerm.mutate({
      id: editingTerm.id,
      data: {
        source: editForm.source,
        target: editForm.target,
        sourceLang: editForm.sourceLang,
        targetLang: editForm.targetLang,
        context: editForm.context,
      },
    })
  }

  const importTerms = useMutation({
    mutationFn: (file: File) => termsApi.import(clientId, file),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['terms', clientId] })
      // res 是 ApiResponse<{count:number}>，count 在 res.data 里
      const count = res.data?.count || 0
      toast.success(`成功导入 ${count} 条术语`)
    },
    onError: (error: any) => {
      toast.error(`导入失败: ${error.message || '未知错误'}`)
    }
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      importTerms.mutate(file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const terms = response?.data || []

  if (isLoading) return <div>加载术语中...</div>

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">客户术语表 ({terms.length})</h3>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={downloadTemplate}
          >
            <Download className="mr-2 h-4 w-4" /> 
            下载模板
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".xlsx"
            onChange={handleFileChange}
          />
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={importTerms.isPending}
          >
            <Upload className="mr-2 h-4 w-4" /> 
            {importTerms.isPending ? '导入中...' : '导入'}
          </Button>
          <Button size="sm" onClick={() => setIsAddDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> 
            新增术语
          </Button>
        </div>
      </div>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新增术语</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>原文语言</Label>
                <Select
                  value={newTerm.sourceLang}
                  onValueChange={(value) => setNewTerm({ ...newTerm, sourceLang: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>译文语言</Label>
                <Select
                  value={newTerm.targetLang}
                  onValueChange={(value) => setNewTerm({ ...newTerm, targetLang: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="source">原文 (Source)</Label>
              <Input
                id="source"
                value={newTerm.source}
                onChange={(e) => setNewTerm({ ...newTerm, source: e.target.value })}
                placeholder="输入原文术语"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="target">译文 (Target)</Label>
              <Input
                id="target"
                value={newTerm.target}
                onChange={(e) => setNewTerm({ ...newTerm, target: e.target.value })}
                placeholder="输入译文"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="context">备注 / 上下文</Label>
              <Input
                id="context"
                value={newTerm.context}
                onChange={(e) => setNewTerm({ ...newTerm, context: e.target.value })}
                placeholder="可选：输入备注或上下文信息"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>取消</Button>
            <Button
              onClick={() => addTerm.mutate(newTerm)}
              disabled={addTerm.isPending || !newTerm.source || !newTerm.target}
            >
              {addTerm.isPending ? '保存中...' : '确定'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 编辑术语对话框：复用新增对话框的字段结构，绑定到 editForm */}
      <Dialog open={!!editingTerm} onOpenChange={(open) => !open && setEditingTerm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>编辑术语</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>原文语言</Label>
                <Select
                  value={editForm.sourceLang}
                  onValueChange={(value) => setEditForm({ ...editForm, sourceLang: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>译文语言</Label>
                <Select
                  value={editForm.targetLang}
                  onValueChange={(value) => setEditForm({ ...editForm, targetLang: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((lang) => (
                      <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-source">原文 (Source)</Label>
              <Input
                id="edit-source"
                value={editForm.source}
                onChange={(e) => setEditForm({ ...editForm, source: e.target.value })}
                placeholder="输入原文术语"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-target">译文 (Target)</Label>
              <Input
                id="edit-target"
                value={editForm.target}
                onChange={(e) => setEditForm({ ...editForm, target: e.target.value })}
                placeholder="输入译文"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-context">备注 / 上下文</Label>
              <Input
                id="edit-context"
                value={editForm.context}
                onChange={(e) => setEditForm({ ...editForm, context: e.target.value })}
                placeholder="可选：输入备注或上下文信息"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTerm(null)}>取消</Button>
            <Button
              onClick={saveEdit}
              disabled={updateTerm.isPending || !editForm.source || !editForm.target}
            >
              {updateTerm.isPending ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>语言</TableHead>
              <TableHead>原文 (Source)</TableHead>
              <TableHead>译文 (Target)</TableHead>
              <TableHead>备注</TableHead>
              <TableHead className="text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {terms.map((term: any) => (
              <TableRow key={term.id}>
                <TableCell className="text-xs font-mono uppercase">
                  <span className="bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                    {term.sourceLang} ➔ {term.targetLang}
                  </span>
                </TableCell>
                <TableCell className="font-medium">{term.source}</TableCell>
                <TableCell>{term.target}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{term.context || '-'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                      onClick={() => openEdit(term)}
                      aria-label="编辑术语"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => deleteTerm.mutate(term.id)}
                      aria-label="删除术语"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {terms.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  暂无术语数据
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
